// src/components/Common/ui/LocalizedDatePicker.tsx
import React, { useEffect, useMemo } from "react";
import { ConfigProvider, DatePicker, TimePicker } from "antd";
import type { DatePickerProps } from "antd";
import type { RangePickerProps } from "antd/es/date-picker";
import type { TimePickerProps, TimeRangePickerProps } from "antd/es/time-picker";
import enUS from "antd/locale/en_US";
import frFR from "antd/locale/fr_FR";
import arEG from "antd/locale/ar_EG";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import "dayjs/locale/ar";
import { useTranslation } from "react-i18next";

const antdLocaleMap: Record<"en" | "fr" | "ar", typeof enUS> = {
  en: enUS,
  fr: frFR,
  ar: arEG,
};
const dateFormatMap: Record<"en" | "fr" | "ar", string> = {
  en: "MM-DD-YYYY",
  fr: "DD-MM-YYYY",
  ar: "DD-MM-YYYY",
};

// helpers
function disablePastDates(current?: Dayjs, includeToday = true) {
  if (!current) return false;
  const todayStart = dayjs().startOf("day");
  return includeToday
    ? current.isBefore(todayStart)
    : current.isSame(dayjs(), "day") || current.isBefore(todayStart);
}

function disableFutureDates(current?: Dayjs, includeToday = true) {
  if (!current) return false;
  const todayEnd = dayjs().endOf("day");
  return includeToday
    ? current.isAfter(todayEnd)
    : current.isSame(dayjs(), "day") || current.isAfter(todayEnd);
}

/* -------------------- DATE PICKER -------------------- */

type LocalizedDatePickerProps = DatePickerProps & {
  fromToday?: boolean;
  pastOnly?: boolean;
  includeToday?: boolean;
};

export const LocalizedDatePicker: React.FC<LocalizedDatePickerProps> = ({
  format,
  placeholder,
  fromToday = false,
  pastOnly = false,
  includeToday = true,
  ...restProps
}) => {
  const { t, i18n } = useTranslation();

  const baseLang = useMemo(
    () =>
      i18n.language.startsWith("fr")
        ? "fr"
        : i18n.language.startsWith("ar")
        ? "ar"
        : "en",
    [i18n.language]
  );

  useEffect(() => {
    dayjs.locale(baseLang);
  }, [baseLang]);

  const antdLocale = useMemo(() => antdLocaleMap[baseLang], [baseLang]);
  const defaultFormat = useMemo(() => dateFormatMap[baseLang], [baseLang]);
  const defaultPlaceholder = t("select_date");
  const direction = baseLang === "ar" ? "rtl" : "ltr";

  const disabledDate = useMemo(() => {
    if (pastOnly) return (d?: Dayjs) => disableFutureDates(d, includeToday);
    if (fromToday) return (d?: Dayjs) => disablePastDates(d, includeToday);
    return undefined;
  }, [pastOnly, fromToday, includeToday]);

  return (
    <ConfigProvider locale={antdLocale} direction={direction}>
      <DatePicker
        format={format || defaultFormat}
        placeholder={placeholder || defaultPlaceholder}
        disabledDate={disabledDate}
        {...restProps}
      />
    </ConfigProvider>
  );
};

type LocalizedRangePickerPropsExt = RangePickerProps & {
  fromToday?: boolean;
  pastOnly?: boolean;
  includeToday?: boolean;
};

export const LocalizedRangePicker: React.FC<LocalizedRangePickerPropsExt> = ({
  format,
  placeholder,
  fromToday = false,
  pastOnly = false,
  includeToday = true,
  ...restProps
}) => {
  const { t, i18n } = useTranslation();

  const baseLang = useMemo(
    () =>
      i18n.language.startsWith("fr")
        ? "fr"
        : i18n.language.startsWith("ar")
        ? "ar"
        : "en",
    [i18n.language]
  );

  useEffect(() => {
    dayjs.locale(baseLang);
  }, [baseLang]);

  const antdLocale = useMemo(() => antdLocaleMap[baseLang], [baseLang]);
  const defaultFormat = useMemo(() => dateFormatMap[baseLang], [baseLang]);
  const defaultPlaceholder: [string, string] = [
    t("start_date"),
    t("end_date"),
  ];
  const direction = baseLang === "ar" ? "rtl" : "ltr";

  const disabledDate = useMemo(() => {
    if (pastOnly) return (d?: Dayjs) => disableFutureDates(d, includeToday);
    if (fromToday) return (d?: Dayjs) => disablePastDates(d, includeToday);
    return undefined;
  }, [pastOnly, fromToday, includeToday]);

  return (
    <ConfigProvider locale={antdLocale} direction={direction}>
      <DatePicker.RangePicker
        format={format || defaultFormat}
        placeholder={(placeholder as [string, string]) || defaultPlaceholder}
        disabledDate={disabledDate}
        {...restProps}
      />
    </ConfigProvider>
  );
};

/* -------------------- TIME PICKER -------------------- */

type LocalizedTimePickerProps = TimePickerProps & {
  // you can add extra props later if needed
};

export const LocalizedTimePicker: React.FC<LocalizedTimePickerProps> = ({
  format,
  placeholder,
  ...restProps
}) => {
  const { t, i18n } = useTranslation();

  const baseLang = useMemo(
    () =>
      i18n.language.startsWith("fr")
        ? "fr"
        : i18n.language.startsWith("ar")
        ? "ar"
        : "en",
    [i18n.language]
  );

  useEffect(() => {
    dayjs.locale(baseLang);
  }, [baseLang]);

  const antdLocale = useMemo(() => antdLocaleMap[baseLang], [baseLang]);
  const direction = baseLang === "ar" ? "rtl" : "ltr";

  const defaultFormat = "HH:mm";
  const defaultPlaceholder = t("select_time");

  return (
    <ConfigProvider locale={antdLocale} direction={direction}>
      <TimePicker
        format={format || defaultFormat}
        placeholder={placeholder || defaultPlaceholder}
        {...restProps}
      />
    </ConfigProvider>
  );
};

type LocalizedTimeRangePickerProps = TimeRangePickerProps & {};

export const LocalizedTimeRangePicker: React.FC<
  LocalizedTimeRangePickerProps
> = ({ format, placeholder, ...restProps }) => {
  const { t, i18n } = useTranslation();

  const baseLang = useMemo(
    () =>
      i18n.language.startsWith("fr")
        ? "fr"
        : i18n.language.startsWith("ar")
        ? "ar"
        : "en",
    [i18n.language]
  );

  useEffect(() => {
    dayjs.locale(baseLang);
  }, [baseLang]);

  const antdLocale = useMemo(() => antdLocaleMap[baseLang], [baseLang]);
  const direction = baseLang === "ar" ? "rtl" : "ltr";

  const defaultFormat = "HH:mm";
  const defaultPlaceholder: [string, string] = [
    t("start_time"),
    t("end_time"),
  ];

  return (
    <ConfigProvider locale={antdLocale} direction={direction}>
      <TimePicker.RangePicker
        format={format || defaultFormat}
        placeholder={
          (placeholder as [string, string]) || defaultPlaceholder
        }
        {...restProps}
      />
    </ConfigProvider>
  );
};

export default LocalizedDatePicker;
